<template>
  <transition name="fade" mode="out-in">
    <div
      v-if="show"
      class="tw-fixed tw-z-40 tw-inset-x-0 tw-overflow-y-auto"
      :style="{ top, height }"
    >
      <div
        class="tw-h-full tw-flex tw-flex-col tw-items-center tw-text-center tw-text-theme"
        :class="[
          bottom ? 'tw-justify-end' : 'tw-justify-center',
          { 'tw-mx-4': $slots.top },
        ]"
      >
        <!-- modal bg -->
        <div
          class="tw-fixed tw-inset-0 tw-transition-opacity"
          @click="handleClickOutside"
        >
          <div
            class="tw-absolute tw-inset-0"
            style="backdrop-filter: blur(4px)"
          />
        </div>

        <slot name="top" />

        <!-- modal -->
        <div
          ref="modalIo"
          :class="[
            Classes.POPUP,
            'tw-w-full tw-align-bottom tw-text-left tw-transform tw-transition-all tw-mx-4 sm:tw-mx-0 sm:tw-align-middle tw-outline-none game-tile-radius',
            modalStyles,
            !imageGallery && modalWidth,
            {
              'tw-flex tw-flex-col tw-border game-primary-button-border-color':
                !imageGallery,
              'tw-my-8 sm:tw-my-12': !$slots.top,
            },
          ]"
          style="box-shadow: 4px 8px 16px 1px rgba(0, 0, 0, 0.2)"
          :style="{
            maxHeight: `calc(min(${containerHeight}px, ${windowHeight}px) - ${MODAL_MARGIN}px)`,
          }"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          tabindex="0"
          @click.self="imageGallery && handleClickOutside()"
        >
          <div
            class="tw-w-full tw-flex"
            :class="[
              ($slots.header || heading) && modalPadX,
              {
                'tw-p-4 game-primary-button-color game-primary-text tw-rounded-t-[calc(var(--tile-border-radius)-1px)]':
                  ($slots.header || heading) && !noPadding,
              },
              imageGallery && 'tw-absolute tw-top-0 tw-left-0',
            ]"
          >
            <base-button
              v-if="backButton"
              auto-width
              icon="angle-left"
              icon-size="xl"
              class="tw-mr-3 tw-w-10 tw-self-center"
              @click="onBack"
            />

            <!-- header slot -->
            <panel-header
              v-if="heading"
              no-padding
              low-weight
              :text="heading"
            />
            <slot v-else name="header" />

            <!-- close button -->
            <base-button
              v-if="hasCloseBtn || imageGallery"
              auto-width
              text-link-hover
              :warning="warning"
              :danger="danger"
              icon="times"
              :icon-size="imageGallery ? 'lg' : 'xl'"
              class="tw-absolute tw-z-2 hover:game-link-color tw-pr-1 tw-pt-1"
              :class="[
                Classes.MODAL_CLOSE_BUTTON,
                imageGallery
                  ? 'tw-text-h4 tw-text-white md:tw-text-h3 tw-top-3 tw-right-3'
                  : 'tw-top-0 tw-right-0 xs:tw-top-1 xs:tw-right-1',
                $slots.header || heading
                  ? 'game-primary-text'
                  : 'game-modal-text-color',
              ]"
              @click="$emit('close:modal', !show)"
            >
            </base-button>
          </div>

          <div
            v-if="$slots.fullWidth"
            class="tw-mx-auto tw-relative tw-overflow-auto tw-util-scrollbar tw-w-full"
            :class="{ 'tw-bg-light': alt }"
          >
            <slot name="fullWidth" />
          </div>

          <!-- modal slot -->
          <div
            v-if="$slots.default"
            class="tw-mx-auto game-modal-text-color"
            :class="[
              modalPadY,
              modalPadX,
              customClass,
              imageGallery && modalWidth,
              {
                'tw-overflow-auto tw-util-scrollbar tw-w-full':
                  !imageGallery && !noScroll,
                'tw-overflow-hidden': noScroll && !showOverflow,
                'tw-overflow-visible tw-w-full': noScroll && showOverflow,
                'tw-w-full tw-h-full': fullWidth,
              },
            ]"
          >
            <slot />
          </div>

          <!-- footer slot -->
          <footer v-if="$slots.footer" class="tw-py-4" :class="[modalPadX]">
            <div
              class="tw-justify-end tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-w-full tw-space-y-2 sm:tw-space-y-0 sm:tw-space-x-4 game-modal-text-color"
            >
              <base-button
                v-if="cancelButton"
                auto-width
                text-link-hover
                @click="$emit('close:modal')"
              >
                {{ $t('cancel') }}
              </base-button>
              <slot name="footer" />
            </div>
          </footer>

          <slot name="controls" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { closeGlobalModal } from '@composables/useModal.js'
import PanelHeader from '@components/PanelHeader.vue'
import { computed, watch, toRefs, ref } from 'vue'
import { onKeyUp } from '@vueuse/core'
import Classes from '@config/css.js'
import {
  containerHeight,
  containerScroll,
  windowHeight,
} from '@composables/useWindowEvents.js'

const MODAL_MARGIN = 96

export default {
  components: {
    PanelHeader,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'lg',
      validator: function (value) {
        return ['sm', 'md', 'lg'].includes(value)
      },
    },
    hasCloseBtn: {
      type: Boolean,
      default: false,
    },
    heading: {
      type: String,
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
    imageGallery: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    noScroll: {
      type: Boolean,
      default: false,
    },
    showOverflow: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: true,
    },
    success: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    alt: {
      type: Boolean,
      default: false,
    },
    cancelButton: {
      type: Boolean,
      default: true,
    },
    // set to false if input within modal is being
    // focused  on load i.e. LoginEmailModal.vue
    autoFocusModal: {
      type: Boolean,
      default: true,
    },
    bottom: {
      type: Boolean,
      default: false,
    },
    backButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close:modal', 'back'],
  setup(props, { emit }) {
    const {
      closeOnClickOutside,
      autoFocusModal,
      closeOnEscape,
      imageGallery,
      noPadding,
      primary,
      warning,
      success,
      danger,
      info,
      size,
      show,
      alt,
    } = toRefs(props)

    const modalIo = ref(null)
    const modalHeight = ref(0)

    const modalWidth = computed(() => {
      if (imageGallery.value) {
        return 'tw-max-w-5/6 xs:tw-max-w-3/4'
      }
      if (size.value === 'lg') {
        return 'tw-max-w-3xl' // 768px
      }
      if (size.value === 'md') {
        return 'tw-max-w-lg' // 512px
      }
      if (size.value === 'sm') {
        return 'tw-max-w-xs' // 320px
      }
      return 'tw-max-w-3xl'
    })

    const modalPadX = computed(() => {
      if (imageGallery.value || noPadding.value) {
        return ''
      }
      return 'tw-px-4'
    })

    const modalPadY = computed(() => {
      if (imageGallery.value || noPadding.value) {
        return ''
      }
      return 'tw-my-4'
    })

    const modalStyles = computed(() => {
      if (imageGallery.value) {
        return 'tw-flex tw-items-center tw-h-full '
      }
      return 'game-modal-background-color tw-inline-block'
    })

    const borderStyle = computed(() => {
      if (success.value) {
        return 'tw-border-success'
      }
      if (danger.value) {
        return 'tw-border-danger'
      }
      if (warning.value) {
        return 'tw-border-warning'
      }
      if (info.value) {
        return 'tw-border-info'
      }
      if (primary.value || alt.value) {
        return 'tw-border-primary'
      }
      return 'tw-border-primary'
    })

    const top = computed(() => {
      if (!containerHeight.value || !containerScroll.value?.frameTop) {
        return '0px'
      }
      const _top = Math.max(
        0,
        Math.min(
          containerHeight.value - modalHeight.value - MODAL_MARGIN,
          -containerScroll.value.frameTop
        )
      )
      return `${_top}px`
    })

    const height = computed(() => {
      const frameBottom = containerHeight.value + containerScroll.value.frameTop
      const frameToWindowBottom =
        windowHeight.value - containerScroll.value.frameTop
      const _height = Math.max(
        Math.min(
          frameBottom,
          windowHeight.value,
          containerHeight.value,
          frameToWindowBottom
        ),
        modalHeight.value + MODAL_MARGIN
      )
      return `${_height}px`
    })

    onKeyUp('Escape', () => {
      if (show.value && closeOnEscape.value) {
        emit('close:modal')
      }
    })

    watch(
      show,
      (value) => {
        if (value) {
          // 160ms timeout to account for component transition to finish
          setTimeout(() => {
            if (modalIo.value) {
              if (autoFocusModal.value) {
                modalIo.value.focus()
              }
              new ResizeObserver(onResize).observe(modalIo.value)
            }
          }, 160)
        }
      },
      { immediate: true }
    )

    function handleClickOutside() {
      if (closeOnClickOutside.value) {
        emit('close:modal')
      }
    }

    function onResize() {
      if (!modalIo.value) return

      modalHeight.value = modalIo.value.scrollHeight
    }

    function onBack() {
      emit('back')
      closeGlobalModal()
    }

    return {
      handleClickOutside,
      containerHeight,
      windowHeight,
      MODAL_MARGIN,
      modalStyles,
      borderStyle,
      modalWidth,
      modalPadY,
      modalPadX,
      Classes,
      modalIo,
      onBack,
      height,
      top,
    }
  },
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0s 0s ease;
}
.fade-enter-from,
.fade-leave-active {
  opacity: 0;
  transition: opacity 0.15s 0.05s ease;
}
</style>
